<script>
    import ElDivider from '~/components/divider/components/divider';
    import EmptyContent from '~/components/empty_content/components/empty_content';

    export default {
        name: 'panel',

        components: {
            ElDivider,
            EmptyContent
        },

        props: {
            title: {
                type: String,
                default: null
            },

            subtitle: {
                type: String,
                default: null
            },

            titleAlignment: {
                type: String,
                default: 'horizontal',
                validate(val) {
                    return ['horizontal', 'vertical'].indexOf(val) !== -1;
                }
            },

            subHeader: {
                type: String,
                default: null
            },

            isLoading: {
                type: Boolean,
                default: false
            },

            noPadding: {
                type: Boolean,
                default: false
            },

            bodyClass: {
                type: [String, Object],
                default: null
            },

            isEmpty: {
                type: Boolean,
                default: false
            },

            emptyIcon: {
                type: String,
                default: null
            },

            emptyTitle: {
                type: String,
                default: 'No Content Found'
            }
        },

        computed: {
            classObject() {
                return [
                    this.bodyClass,
                    {'panel-body--no-padding': this.noPadding}
                ]
            },

            hasHeader() {
                return this.$slots.header
                    || this.$slots.headerRight
                    || this.$slots.headerLeft
                    || this.hasTitle
                    || this.hasSubtitle
                    || this.subHeader;
            },

            hasTitle() {
                return this.title || this.$slots.title;
            },

            hasSubtitle() {
                return this.subtitle || this.$slots.subtitle;
            },

            showTitleDivider() {
                return this.$slots.headerLeft
                    && this.titleAlignment === 'vertical'
                    && (this.hasTitle || this.hasSubtitle);
            },
        }
    }
</script>

<template>
    <div class="panel panel-inverse">
        <div class="panel-heading relative border-bottom-1" v-if="hasHeader">
            <div class="panel-heading__top d-flex align-items-center">
                <slot name="header"></slot>
                <div :class="['panel-title-container', 'panel-title-container__' + titleAlignment]">
                    <h4 class="panel-title" v-if="hasTitle">
                        <slot name="title">
                            {{title}}
                        </slot>
                    </h4>
                    <h4 class="panel-subtitle" v-if="hasSubtitle">
                        <slot name="subtitle">
                            {{subtitle}}
                        </slot>
                    </h4>
                </div>

                <el-divider v-if="showTitleDivider" class="panel-heading__divider" direction="vertical"></el-divider>

                <div class="panel-heading-left" v-if="$slots.headerLeft">
                    <slot name="headerLeft"></slot>
                </div>

                <div class="panel-heading-right ml-auto" v-if="$slots.headerRight">
                    <slot name="headerRight"></slot>
                </div>

                <transition
                        appear
                        name="custom-classes-transition"
                        enter-active-class="animated fadeIn"
                        leave-active-class="animated fadeOut">

                    <div class="loader loader--panel" v-if="isLoading">
                        <div class="indeterminate"></div>
                    </div>
                </transition>
            </div>

            <div class="panel-heading__sub-header d-flex align-items-center" v-if="subHeader">
                {{subHeader}}
            </div>
        </div>

        <template v-if="isEmpty">
            <div class="panel-body">
                <slot name="empty">
                    <empty-content
                            :icon="emptyIcon"
                            :title="emptyTitle">
                    </empty-content>
                </slot>
            </div>
        </template>

        <template v-else>
            <template v-if="$slots.secondHeader">
                <div class="panel-secondary-header">
                    <slot name="secondHeader"></slot>
                </div>
            </template>

            <slot name="above-body"></slot>

            <div class="panel-body" :class="this.classObject">
                <slot name="body"></slot>
            </div>

            <template v-if="$slots.footer">
                <div class="panel-footer">
                    <slot name="footer"></slot>
                </div>
            </template>
        </template>
    </div>
</template>